// RPC serveur du paiement d'abonnement (paiement local manuel vérifié par preuve).
//
// Toute la logique sensible est côté base (SECURITY DEFINER) :
//   - `soumettre_paiement_abonnement` : contrôle le compte, la formule, la
//     périodicité et calcule le MONTANT OFFICIEL côté serveur (le client ne
//     fournit jamais de montant). Pour Orange Money / Moov Money, la référence
//     et la preuve sont obligatoires. Le paiement créé reste `en_attente`.
//   - `traiter_paiement_abonnement` : réservé au Super Admin. Valide ou refuse
//     un paiement, active l'abonnement en cas de validation et journalise.
//
// Aucune donnée fictive : uniquement des appels RPC réels.

import { supabase } from "@/store/shared/supabase";
import type { FormuleId, Periodicite } from "@/config/formules";
import type { PaiementMethode } from "@/config/paiement";
import type { MonAbonnement } from "./abonnement";

/** Soumet une demande de paiement d'abonnement (formule + périodicité + moyen). */
export async function soumettrePaiementAbonnementRpc(params: {
  formuleCode: FormuleId;
  periodicite: Periodicite;
  methode: PaiementMethode;
  reference: string;
  preuveUrl: string;
}): Promise<MonAbonnement | null> {
  const { data, error } = await supabase.rpc("soumettre_paiement_abonnement", {
    p_formule_code: params.formuleCode,
    p_periodicite: params.periodicite,
    p_mode: params.methode,
    p_reference: params.reference,
    p_preuve_url: params.preuveUrl,
  });
  if (error) throw error;
  return (data as MonAbonnement) ?? null;
}

/** Valide ou refuse un paiement d'abonnement (Super Admin uniquement). */
export async function traiterPaiementAbonnementRpc(
  paiementId: string,
  valider: boolean,
  motif?: string
): Promise<void> {
  const { error } = await supabase.rpc("traiter_paiement_abonnement", {
    p_paiement_id: paiementId,
    p_valider: valider,
    p_motif: motif ?? null,
  });
  if (error) throw error;
}

/**
 * Soumet une demande de paiement liée au compte du nouvel inscrit (encore `en_attente`).
 *
 * Utilisée à la PREMIÈRE CONNEXION : la confirmation e-mail empêche d'avoir une
 * session pendant l'inscription, donc la preuve et l'envoi se finalisent ici.
 * La RPC `soumettre_paiement_inscription` déduit le compte de la session Auth
 * (jamais d'un identifiant fourni par le navigateur) et recalcule le montant
 * officiel côté serveur. Le paiement créé reste `en_attente`.
 */
export async function soumettrePaiementInscriptionRpc(params: {
  formuleCode: FormuleId;
  periodicite: Periodicite;
  methode: PaiementMethode;
  reference: string;
  preuveUrl: string;
}): Promise<void> {
  const { error } = await supabase.rpc("soumettre_paiement_inscription", {
    p_formule_code: params.formuleCode,
    p_periodicite: params.periodicite,
    p_mode: params.methode,
    p_reference: params.reference,
    p_preuve_url: params.preuveUrl,
  });
  if (error) throw error;
}